<template>
  <v-card flat v-bind="$attrs" v-on="$listeners" :loading="isLoading">
    <div class="text-center" v-if="isLoading">
      <v-progress-circular indeterminate color="primary" />
    </div>
    <v-form ref="participantForm" @submit.prevent="onSubmit" v-else v-model="validForm">
      <v-container>
        <v-row>
          <v-col class="pb-0">
            <v-alert border="top" type="info">
              Welcome {{ userForm.fullName }}. Please use the form below to update your information.
            </v-alert>
            <v-alert dismissible border="top" type="success" v-if="profileUpdated">
              Contact Updated Successfully
            </v-alert>
            <slot v-if="!isModifiable" name="messageWhenParticipantTabIsNotEditable">
              Below is your information on file
            </slot>
          </v-col>
        </v-row>

        <v-row v-if="showName && userForm">
          <v-col cols="12" sm="12" md="4" lg="4" xl="4">
            <v-text-field
              id="firstName"
              name="firstName"
              v-model="userForm.firstName"
              :label="$i18n.translate('First Name')"
              dense
              :class="requireName ? 'required' : ''"
              :disabled="!isModifiable"
              :rules="rules.firstName"
              :error-messages="$error.getValidationError(errors, 'firstName')"
              :maxlength="fieldMaxLengths.USER_FIRST_NAME"
              :counter="fieldMaxLengths.USER_FIRST_NAME"
              @input="$error.clearValidationError(errors, 'firstName')"
            />
          </v-col>
          <v-col cols="12" sm="12" md="4" lg="4" xl="4">
            <v-text-field
              id="middleName"
              name="middleName"
              v-model="userForm.middleName"
              :label="$i18n.translate('Middle Name')"
              dense
              :disabled="!isModifiable"
              :rules="rules.middleName"
              :maxlength="fieldMaxLengths.USER_MIDDLE_NAME"
              :counter="fieldMaxLengths.USER_MIDDLE_NAME"
            />
          </v-col>
          <v-col cols="12" sm="12" md="4" lg="4" xl="4">
            <v-text-field
              id="lastName"
              name="lastName"
              v-model="userForm.lastName"
              :label="$i18n.translate('Last Name')"
              dense
              :class="requireName ? 'required' : ''"
              :disabled="!isModifiable"
              :rules="rules.lastName"
              :error-messages="$error.getValidationError(errors, 'lastName')"
              :maxlength="fieldMaxLengths.USER_LAST_NAME"
              :counter="fieldMaxLengths.USER_LAST_NAME"
              @input="$error.clearValidationError(errors, 'lastName')"
            />
          </v-col>
        </v-row>
        <v-row v-if="showEmail">
          <v-col>
            <EmailField
              dense
              name="email1"
              :label="$i18n.translate('Email')"
              v-model="participantForm.email1"
              :class="requireEmail ? 'required' : ''"
              :rules="rules.email1"
              :error-messages="$error.getValidationError(errors, 'email1')"
              @input="$error.clearValidationError(errors, 'email1')"
              :disabled="!isModifiable"
            />
          </v-col>
        </v-row>
        <v-row v-if="showAddress">
          <v-col cols="12" sm="12" md="6" lg="6" xl="6">
            <v-text-field
              dense
              name="address1"
              :label="$i18n.translate('Address') + ' 1'"
              v-model="participantForm.address.address1"
              :class="requireAddress ? 'required' : ''"
              :rules="rules.address1"
              :error-messages="$error.getValidationError(errors, 'address1')"
              @input="$error.clearValidationError(errors, 'address1')"
              :disabled="!isModifiable"
              :maxlength="fieldMaxLengths.ADDRESS_LINE_1"
              :counter="fieldMaxLengths.ADDRESS_LINE_1"
            />
          </v-col>
          <v-col cols="12" sm="12" md="6" lg="6" xl="6">
            <v-text-field
              dense
              name="address2"
              :label="$i18n.translate('Address') + ' 2'"
              v-model="participantForm.address.address2"
              :rules="rules.address2"
              :error-messages="$error.getValidationError(errors, 'address2')"
              @input="$error.clearValidationError(errors, 'address2')"
              :disabled="!isModifiable"
              :maxlength="fieldMaxLengths.ADDRESS_LINE_2"
              :counter="fieldMaxLengths.ADDRESS_LINE_2"
            />
          </v-col>
        </v-row>

        <v-row v-if="showAddress">
          <v-col cols="12" sm="12" md="4" lg="4" xl="4">
            <v-text-field
              dense
              name="city"
              :label="$i18n.translate('City')"
              v-model="participantForm.address.city"
              :class="requireAddress ? 'required' : ''"
              :rules="rules.city"
              :error-messages="$error.getValidationError(errors, 'city')"
              @input="$error.clearValidationError(errors, 'city')"
              :disabled="!isModifiable"
              :maxlength="fieldMaxLengths.ADDRESS_CITY"
              :counter="fieldMaxLengths.ADDRESS_CITY"
            />
          </v-col>
          <v-col cols="12" sm="12" md="4" lg="4" xl="4">
            <RegionField
              dense
              name="region"
              v-model="participantForm.address.region"
              :country="participantForm.address.country"
              :class="requireAddress ? 'required' : ''"
              :rules="rules.region"
              :error-messages="$error.getValidationError(errors, 'region')"
              @input="$error.clearValidationError(errors, 'region')"
              :disabled="!isModifiable"
            />
          </v-col>
          <v-col cols="12" sm="12" md="4" lg="4" xl="4">
            <CountryField
              dense
              name="country"
              :label="$i18n.translate('Country')"
              v-model="participantForm.address.country"
              :program="selectedProgram"
              :class="requireAddress ? 'required' : ''"
              :rules="rules.country"
              :error-messages="$error.getValidationError(errors, 'country')"
              @input="$error.clearValidationError(errors, 'country')"
              :disabled="!isModifiable || isCountryFieldDisabled"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="12" md="4" lg="4" xl="4" v-if="showAddress">
            <PostalCodeField
              dense
              name="postalCode"
              v-model="participantForm.address.postalCode"
              :country="participantForm.address.country"
              :class="requireAddress ? 'required' : ''"
              :rules="rules.postalCode"
              :error-messages="$error.getValidationError(errors, 'postalCode')"
              @input="$error.clearValidationError(errors, 'postalCode')"
              :disabled="!isModifiable"
            />
          </v-col>
          <v-col cols="12" sm="12" md="4" lg="4" xl="4" v-if="showPhoneNumber1">
            <PhoneNumberField
              dense
              name="phoneNumber1"
              :label="this.selectedProgram.phoneType1Description"
              v-model="participantForm.phoneNumber1"
              :country="participantForm.address.country"
              :class="requirePhoneNumber1 ? 'required' : ''"
              :rules="rules.phoneNumber"
              :error-messages="$error.getValidationError(errors, 'phoneNumber1')"
              @input="$error.clearValidationError(errors, 'phoneNumber1')"
              :disabled="!isModifiable"
            />
          </v-col>
          <v-col cols="12" sm="12" md="4" lg="4" xl="4" v-if="showGender">
            <GenderField
              dense
              name="gender"
              id="gender"
              v-model="userForm.gender"
              :label="$i18n.translate('Gender')"
              :class="requireGender ? 'required' : ''"
              :rules="requireGender ? rules.gender : undefined"
              :error-messages="$error.getValidationError(errors, 'gender')"
              @input="$error.clearValidationError(errors, 'gender')"
              autocomplete="off"
              :disabled="!isModifiable"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="12" md="4" lg="4" xl="4" v-if="showTin">
            <TinField
              dense
              name="tin"
              id="tin"
              v-model="userForm.transientTin"
              :country="participantForm.address.country"
              :user="userForm"
              :class="requireTin ? 'required' : ''"
              :rules="requireTin ? rules.tin : undefined"
              :disabled="$auth.isImpersonating() || !isModifiable || !participantForm.address.country"
              :tooltip="tooltipMessage"
              :saveOnBlur="true"
            />
          </v-col>
          <v-col cols="12" sm="12" md="4" lg="4" xl="4" v-if="showBirthDate">
            <DateTimePickerField
              dense
              v-model="userForm.birthDate"
              :disabled="!isModifiable"
              :label="$i18n.translate('Birth Date')"
              :class="requireBirthDate ? 'required' : ''"
              :rules="requireBirthDate ? rules.birthDate : undefined"
              :error-messages="$error.getValidationError(errors, 'birthDate')"
              @input="$error.clearValidationError(errors, 'birthDate')"
              onlyDate
              format="MM/DD/YYYY"
              :max="today"
            />
          </v-col>
        </v-row>
        <v-row v-if="participantCustomFields.length">
          <v-col
            v-for="field in participantCustomFields"
            :key="`profile-custom-field-${field.id}-${field.programIndex}`"
            cols="12"
            md="4"
          >
            <CustomField
              dense
              :custom-field="field"
              v-model="participantForm[`customFieldValue${field.programIndex + 1}`]"
              :disabled="!isModifiable"
            />
          </v-col>
        </v-row>

        <v-row v-if="showTermsAndConditions">
          <v-col>
            <TermsAndConditionsView
              :value="termsAndConditions"
              @update:valid="validTermsAndConditions = $event"
              :required="selectedParticipant.participantType.requireTermsAndConditions === 'REQUIRED'"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-btn v-if="isModifiable" type="submit" color="primary" @click="onSubmit" :disabled="!validForm">
              Update
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
  </v-card>
</template>

<script>
import TermsAndConditionsView from "@/gapp-components/components/display/TermsAndConditionsView.vue";
import CountryField from "@/gapp-components/components/fields/CountryField";
import DateTimePickerField from "@/gapp-components/components/fields/DateTimePickerField.vue";
import EmailField from "@/gapp-components/components/fields/EmailField";
import GenderField from "@/gapp-components/components/fields/GenderField.vue";
import PhoneNumberField from "@/gapp-components/components/fields/PhoneNumberField";
import PostalCodeField from "@/gapp-components/components/fields/PostalCodeField";
import RegionField from "@/gapp-components/components/fields/RegionField";
import TinField from "@/gapp-components/components/fields/TinField";
import ApiService from "@/gapp-components/services/api.service";
import ValidationService from "@/gapp-components/services/validation.service.js";
import { mapGetters } from "vuex";
import ParticipantService from "@/gapp-components/services/participant.service.js";
import CustomField from "@/gapp-components/components/fields/CustomField.vue";

import FIELD_MAX_LENGTHS from "@/gapp-components/utils/fieldMaxLengths";

export default {
  name: "ProfileParticipantTab",
  components: {
    CountryField,
    RegionField,
    PostalCodeField,
    PhoneNumberField,
    TinField,
    EmailField,
    TermsAndConditionsView,
    GenderField,
    DateTimePickerField,
    CustomField
  },

  inject: {
    participantCustomFields: {
      default: () => []
    }
  },

  data: () => ({
    fieldMaxLengths: FIELD_MAX_LENGTHS,
    validForm: false,
    isLoading: true,
    isModifiable: false,
    isCountryFieldDisabled: true,
    userForm: {},
    participantForm: {
      address: {
        address1: "",
        country: {},
        region: {}
      }
    },
    intialParticipantForm: {},
    participantChangeLogs: [],
    today: new Date().toISOString(),
    participantType: null,
    errors: {},
    profileUpdated: false,
    validTermsAndConditions: false,
    termsAndConditions: {},
    tooltipMessage:
      "Some activities and earnings within this program are taxable and require us to file a 1099 or T4A. Please provide your Tax Identification number in order to earn in this program."
  }),
  methods: {
    ensureParticipantCustomFieldValues() {
      this.participantCustomFields.forEach(field => {
        const key = `customFieldValue${field.programIndex + 1}`;

        if (!(key in this.participantForm)) {
          this.$set(this.participantForm, key, null);
        }
      });
    },

    fetchData() {
      this.isLoading = true;
      this.errors = {};
      ApiService.get(`/api/participants/${this.selectedParticipant.id}`)
        .then(({ data }) => {
          this.participantForm = data;

          this.ensureParticipantCustomFieldValues();

          this.intialParticipantForm = JSON.parse(JSON.stringify(data));
          this.isModifiable = !(this.participantForm.source && this.participantForm.source.length > 0);
          this.participantForm.address = this.participantForm.address ? this.participantForm.address : {};
          this.isCountryFieldDisabled = this.participantForm.address.country == undefined ? false : true;

          return this.$api.getRelatedObject("user", data).then(({ data }) => {
            this.userForm = data;
            this.updateLastFourDigits(data.tin);
          });
        })
        .catch(error => {
          this.errors = ApiService.getErrorsFromResponse(error);
        })
        .finally(() => {
          this.isLoading = false;
          this.$nextTick(() => {
            if (this.$refs && this.$refs.participantForm) {
              this.$refs.participantForm.validate();
            }
          });
          this.$emit("onLoad");
        });
    },
    onSubmit() {
      if (!this.isModifiable) {
        return;
      }

      this.isLoading = true;
      this.errors = {};

      let postParticipantForm = Object.assign({}, this.participantForm);
      let postUserForm = {
        firstName: this.userForm.firstName,
        middleName: this.userForm.middleName,
        lastName: this.userForm.lastName,
        gender: this.userForm.gender,
        birthDate: this.userForm.birthDate
      };

      if (this.validTermsAndConditions) {
        postParticipantForm.termsAndConditionsAccepted = true;
      }
      if (this.participantForm.address && this.participantForm.address.city) {
        this.participantForm.address.city = this.participantForm.address.city.trim();
      }

      Promise.all([
        this.$api.patch(`/api/participants/${this.selectedParticipant.id}`, postParticipantForm).then(({ data }) => {
          this.participantForm = data;
          if (!this.participantForm.address) {
            this.participantForm.address = {};
          }
        }),
        this.$api.patch(`/api/users/${this.userForm.id}`, postUserForm).then(({ data }) => {
          this.userForm = data;
        })
      ])
        .then(() => {
          this.$api.get("/api/participants/bySelectedParticipant").then(({ data }) => {
            this.userForm = data.user;

            this.$auth.storeSelectParticipant(data).then(() => {
              window.scrollTo(0, 0);
              this.profileUpdated = true;
              setTimeout(() => {
                this.profileUpdated = false;
              }, 4000);
              return data;
            });
          });
        })
        .catch(error => {
          this.errors = this.$api.getErrorsFromResponse(error);
        })
        .finally(() => {
          this.participantChangeLogs = ParticipantService.checkParticipantChanges(
            this.intialParticipantForm,
            this.participantForm
          );

          this.participantChangeLogs.forEach(msg => {
            ParticipantService.logParticipant(this.selectedParticipant, msg, this.selectedParticipant.user);
          });

          this.participantChangeLogs = [];
          this.isLoading = false;
          this.fetchData();
        });
    },
    updateLastFourDigits(ssn) {
      if (this.participantForm.address.country) {
        let aux = this.participantForm.address.country.participantTinMask;
        let counter2 = 3;
        for (let i = aux.length - 1; i >= 0; i--) {
          if (aux.substr(i, 1) == "#") {
            if (counter2 >= 0) {
              aux = aux.substr(0, i) + ssn.substr(counter2, 1) + aux.substr(i + 1);
              counter2--;
            }
          }
        }
        this.lastFourDigits = aux.replaceAll("#", "*");
      }
    },
    getTermsAndConditions() {
      if (this.selectedLocale) {
        let selectedLocaleName = this.selectedLocale.languageType.name;
        if (this.selectedProgram && this.selectedProgram.defaultTermsAndConditions) {
          let termsAndConditions = this.selectedProgram.defaultTermsAndConditions.filter(
            tac => tac.language.name == selectedLocaleName
          );
          if (termsAndConditions.length > 0) {
            this.termsAndConditions = termsAndConditions[0];
          }
        }
      }
    }
  },
  watch: {
    validForm(newValue) {
      if (typeof newValue == "boolean") {
        this.$emit("input", this.validForm);
      }
    }
  },
  mounted() {
    this.getTermsAndConditions();
    this.fetchData();
    this.validForm = this.value;
  },
  computed: {
    ...mapGetters(["selectedParticipant", "selectedProgram", "selectedLocale"]),

    showAddress() {
      return this.selectedParticipant.participantType.requireAddress != "INVISIBLE";
    },
    showBirthDate() {
      return this.selectedParticipant.participantType.requireBirthDate != "INVISIBLE";
    },
    showEmail() {
      return this.selectedParticipant.participantType.requireEmail != "INVISIBLE";
    },
    showGender() {
      return this.selectedParticipant.participantType.requireGender != "INVISIBLE";
    },
    showName() {
      return this.selectedParticipant.participantType.requireName != "INVISIBLE";
    },
    showPhoneNumber1() {
      return this.selectedParticipant.participantType.requirePhone != "INVISIBLE";
    },
    showTermsAndConditions() {
      return (
        !this.participantForm.termsAndConditionsAccepted &&
        this.selectedParticipant.participantType.requireTermsAndConditions != "INVISIBLE"
      );
    },
    showTin() {
      return this.selectedParticipant.participantType.requireTin != "INVISIBLE";
    },

    requireAddress() {
      return this.isModifiable && this.selectedParticipant.participantType.requireAddress === "REQUIRED";
    },
    requireBirthDate() {
      return this.isModifiable && this.selectedParticipant.participantType.requireBirthDate === "REQUIRED";
    },
    requireEmail() {
      return this.isModifiable && this.selectedParticipant.participantType.requireEmail === "REQUIRED";
    },
    requireGender() {
      return this.isModifiable && this.selectedParticipant.participantType.requireGender === "REQUIRED";
    },
    requireName() {
      return this.isModifiable && this.selectedParticipant.participantType.requireName === "REQUIRED";
    },
    requirePhoneNumber1() {
      return this.isModifiable && this.selectedParticipant.participantType.requirePhone === "REQUIRED";
    },
    requireTermsAndConditions() {
      return (
        this.canEdit &&
        this.isModifiable &&
        !this.participantForm.termsAndConditionsAccepted &&
        this.selectedParticipant.participantType.requireTermsAndConditions === "REQUIRED"
      );
    },
    requireTin() {
      if (this.$auth.isImpersonating()) return false;

      return this.isModifiable && this.selectedParticipant.participantType.requireTin === "REQUIRED";
    },

    rules() {
      return {
        firstName: [
          ValidationService.generateNotWhitespacesRule("First Name"),
          ValidationService.generateNameCharacterValidationRule("First Name"),
          ...ValidationService.generateTextValidationRules("First Name", 100, this.requireName)
        ],
        middleName: [
          ValidationService.generateNotWhitespacesRule("Middle Name"),
          ValidationService.generateNameCharacterValidationRule("Middle Name"),
          ...ValidationService.generateTextValidationRules("Middle Name", 100, false)
        ],
        lastName: [
          ValidationService.generateNotWhitespacesRule("Last Name"),
          ValidationService.generateNameCharacterValidationRule("Last Name"),
          ...ValidationService.generateTextValidationRules("Last Name", 100, this.requireName)
        ],
        tin: this.requireTin ? ValidationService.generateTextValidationRules("TIN") : [],
        address1: [
          ValidationService.generateNotWhitespacesRule("Address 1"),
          ...ValidationService.generateAddressValidationRules("Address 1", 200, this.requireAddress)
        ],
        address2: [
          ValidationService.generateNotWhitespacesRule("Address 2"),
          ...ValidationService.generateAddressValidationRules("Address 2", 200, false)
        ],
        city: [
          ValidationService.generateNotWhitespacesRule("City"),
          ...ValidationService.generateTextValidationRules("City", 100, this.requireAddress)
        ],
        country: this.requireAddress ? ValidationService.generateDropdownValidationRules("Country") : [],
        region: this.requireAddress ? ValidationService.generateDropdownValidationRules("Region") : [],
        postalCode: this.requireAddress ? ValidationService.generateTextValidationRules("Postal Code") : [],
        phoneNumber: this.requirePhoneNumber1 ? ValidationService.generateTextValidationRules("Phone Number") : [],
        email1: this.requireEmail ? ValidationService.generateEmailValidationRules("Email") : [],
        gender: this.requireGender ? ValidationService.generateDropdownValidationRules("Gender") : [],
        birthDate: this.requireBirthDate ? ValidationService.generateDateValidationRules("Birth Date") : []
      };
    }
  }
};
</script>
